import { usePWA } from 'common/composition/usePWA.js'
import { nativeBridge } from 'common/native-bridge.js'
import device from 'common/platform'

const NATIVE_DEVICE_ID = 'NATIVE_DEVICE_ID'
const NATIVE_AD_ID = 'NATIVE_AD_ID'

function hasNativeId(value) {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  return typeof value === 'bigint' || (typeof value === 'number' && Number.isSafeInteger(value))
}

function readStorageValue(storageName, key) {
  try {
    const value = window[storageName]?.getItem(key)
    return hasNativeId(value) ? value : undefined
  } catch {
    return undefined
  }
}

function readStoredNativeId(key) {
  return readStorageValue('sessionStorage', key) ?? readStorageValue('localStorage', key)
}

export function useAntibotRuntimeContext() {
  const PWA = usePWA()
  const isWebView = process.env.WEBVIEW === '1'
  const listeners = new Set()

  let vendorDeviceId
  let vendorAdId

  function getRuntimeMode() {
    if (isWebView) {
      return 'webview'
    }

    PWA.checkPWA()
    return PWA.isPwa.value ? 'pwa' : 'web'
  }

  const runtimeMode = getRuntimeMode()

  function getContext() {
    return {
      runtimeMode,
      ...(vendorDeviceId !== undefined ? { vendorDeviceId } : {}),
      ...(vendorAdId !== undefined ? { vendorAdId } : {}),
    }
  }

  function notifyContextChanged() {
    const context = getContext()
    listeners.forEach((listener) => listener(context))
  }

  function setVendorDeviceId(value) {
    if (!hasNativeId(value) || value === vendorDeviceId) {
      return
    }

    vendorDeviceId = value
    notifyContextChanged()
  }

  function setVendorAdId(value) {
    if (!hasNativeId(value) || value === vendorAdId) {
      return
    }

    vendorAdId = value
    notifyContextChanged()
  }

  function handleNativeDeviceId(value) {
    setVendorDeviceId(hasNativeId(value) ? value : readStoredNativeId(NATIVE_DEVICE_ID))
  }

  function handleNativeAdId(value) {
    setVendorAdId(hasNativeId(value) ? value : readStoredNativeId(NATIVE_AD_ID))
  }

  if (isWebView && device.is.android) {
    nativeBridge.once(NATIVE_DEVICE_ID, handleNativeDeviceId)
    nativeBridge.once(NATIVE_AD_ID, handleNativeAdId)

    setVendorDeviceId(readStoredNativeId(NATIVE_DEVICE_ID))
    setVendorAdId(readStoredNativeId(NATIVE_AD_ID))
  } else if (isWebView && device.is.ios) {
    setVendorDeviceId(new URLSearchParams(window.location.search).get('vendor_device_id'))
  }

  function onChange(listener) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  return { getContext, onChange }
}
